import { type ThunkAction } from "redux-thunk";
import { type LineItem, PackageEntrySource } from "../../models/CartData";
import { type AnyAction } from "~/app/store/types";
import { navigateToProductContract } from "~/features/contracts/utils/utils";
import { getNextUID } from "~/common/utils/uid";
import { fetchContractsOverview } from "~/features/contracts/api/getContractsOverview";
import { orchestrateARefreshOnCheckout, orchestratePnARefresh } from "./cartRefresh";
import {
  buildCartMaterialNos,
  newChangeObject,
  newCopyRowObject,
  newUpdatesObject,
} from "../../../../../utils/cartUpdating/common";
import { refreshPriceInfoData } from "../../../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveriesForProduct } from "../../../../../utils/cartUpdating/delivery/refreshEdd";
import { reconcileCartProductEntries, sendToHybris, sendToRedux } from "./cartQuoteCommonActions";
import { getActiveCartData } from "../../api/CartView";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { addMessage } from "~/features/toasts/redux/actions";
import { type RootState } from "~/app/store";
import { getDesciptorsForLineItems } from "./actionUtils";
import {
  type CartPnaPair,
  loadCartPna,
  quotePackCodeFromBuilders,
  quotePackCodeFromEntries,
  resolveCartDescriptor,
} from "../../pna/cartPna";
import { selectLineItemsByProduct, selectPackageEntry, selectProductCodeFromPackage } from "../selectors/cartSelectors";
import {
  MSG_COULD_NOT_PRESELECT_PACKAGE,
  MSG_MISSING_PACKAGE_REGULAR,
  MSG_PACKAGE_CHANGED,
  refreshAvailabilityLoadNonContract,
  refreshPackage,
} from "../../../../../utils/cartUpdating/refreshPackage";
import { groupByFn } from "~/common/utils/reducers";
import { validatePackage, validateQty } from "../../../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../../../utils/cartUpdating/validations/validateGeneralProduct";
import { getFirstValidDate } from "~/common/utils/dateUtils";
import debounce from "lodash/debounce";
import { selectLineItemsForProduct } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { refreshDeliveryDate } from "~/utils/cartUpdating/delivery/refreshDate";
import { cartLoadStartAction } from "~/features/cart/cart/redux/actions";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { apiUpdateCartComment } from "~/features/cart/cart/api/updateCartComment";
import { CommentTargets, trackCommentActions } from "~/app/analytics/ga/salesPartner";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import { BLOCK_SCREEN, UNBLOCK_SCREEN } from "~/features/cart/cart/redux/actions/actionTypes";
import uniqBy from "lodash/uniqBy";
import { ProductService } from "~/features/addToCart/api/ProductService";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { resolveActiveCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";
import { batch } from "react-redux";

export {
  deletePackageEntry,
  clearCart,
  updateQuantity,
  updateDate,
  updateComment,
  toggleCommentVisibility,
} from "./cartQuoteCommonActions";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

/** Imperatively retries every cart product's PnA through the canonical Query commands. */
export function loadAllPnAAction(): ThunkResult<void> {
  return (dispatch, getState) => {
    const productCodes = Object.keys(selectLineItemsByProduct(getState()));

    batch(() => {
      productCodes.forEach((productCode) => dispatch(loadPnACartEntry(productCode)));
    });
  };
}

interface SwitchProductToNewVersionProps {
  productCode: string;
  successorProductCode: string;
  packEntryId: number;
  priceReference: string;
}

export function copyLastPackageEntry(lastEntryId: number): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    const lastEntry = state.cart.packageEntriesById[lastEntryId];

    const allLineItems = selectLineItemsForProduct(state, { productCode: lastEntry.productCode }).map(newChangeObject);

    const copiedLineItem = newCopyRowObject(lastEntry);
    copiedLineItem.set("id", getNextUID());

    if (lastEntry.source !== PackageEntrySource.CONTRACT && !copiedLineItem.get("deliveryDate")) {
      copiedLineItem.set("manuallySetRDD", true);
      copiedLineItem.set("deliveryDate", getFirstValidDate());
    }

    copiedLineItem.set("edd", null);
    copiedLineItem.set("isInStock", null);
    copiedLineItem.set("customerOrderNumber", "");
    if (copiedLineItem.get("isBundled")) {
      copiedLineItem.set("original", false);
    }
    allLineItems.push(copiedLineItem);

    const nonContractLineItems = allLineItems.filter((item) => !item.get("contractNumber"));

    const productDescriptors = getDesciptorsForLineItems(state, lastEntry.productCode, allLineItems);
    refreshPriceInfoData(allLineItems, productDescriptors);

    const productDescriptor = productDescriptors[copiedLineItem.get("priceReference")];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(nonContractLineItems, availability);
    }

    validateEntireProductQty(allLineItems, productDescriptor, buildCartMaterialNos(state));

    dispatch(sendToRedux(allLineItems));
    dispatch(cartLoadStartAction());
    await dispatch(sendToHybris(allLineItems));
    dispatch(cartPageLoadThunk());

    if (lastEntry.source !== PackageEntrySource.CONTRACT) {
      const lineItem = copiedLineItem.build() as LineItem;
      AnalyticsMQ.instance.push("addToCart", {
        products: {
          product: productDescriptor.base,
          packaging: {
            code: lineItem.packCode,
            size: lineItem.packSize ?? 1,
          },
          price: lineItem.netPrice,
          quantity: lineItem.quantity,
        },
        context: {
          source: "unknown",
          recommendationType: lineItem.recoType,
          recommendationPlace: lineItem.recoPlace,
        },
      });
    }
  };
}

export const switchProductVersion = (): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const packEntries = Object.values(state.cart.packageEntriesById);
    const lineItemsToUpdate: LineItemBuilder[] = [];
    const modifiablePackEntries: SwitchProductToNewVersionProps[] = [];

    packEntries.forEach((packEntry) => {
      const productCode = packEntry.productCode;
      const base = state.products.basic[productCode];
      const successorProduct = base?.successorProduct;
      if (successorProduct) {
        const successorProductCode = successorProduct.code;
        modifiablePackEntries.push({
          productCode,
          successorProductCode,
          packEntryId: packEntry.id,
          priceReference: packEntry.priceReference,
        });
      }
    });

    // Consume the awaited successor descriptors directly — no dictionary re-read afterwards.
    const successorDescriptors = await Promise.all(
      modifiablePackEntries.map(
        ({ successorProductCode, priceReference }) =>
          dispatch(
            ProductService.getProductDescriptor(successorProductCode, priceReference, {
              fetch: true,
            }),
          ).promise as Promise<ProductDescriptor>,
      ),
    );

    modifiablePackEntries.forEach(({ productCode, successorProductCode, packEntryId, priceReference }, index) => {
      const productDescriptor = successorDescriptors[index];
      const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
      const editedLineItem = lineItems.find((item) => item.get("id") === packEntryId);

      if (editedLineItem && productDescriptor) {
        editedLineItem.set("successorProductCode", successorProductCode);
        editedLineItem.set("packCode", INVALID_PACKAGE_CODE);
        lineItemsToUpdate.push(editedLineItem);

        const availability = productDescriptor.availability as AvailabilityInfo;
        refreshPriceInfoData([editedLineItem], { [priceReference]: productDescriptor });
        refreshAvailabilityLoadNonContract(editedLineItem, availability, productDescriptor);
      }
    });

    dispatch(sendToRedux(lineItemsToUpdate));
    const switchProductPromise = dispatch(sendToHybris(lineItemsToUpdate));
    switchProductPromise.then((res) => {
      if (res) {
        const url = new URL(window.location.href);
        url.searchParams.set("switched-products-count", `${uniqBy(modifiablePackEntries, "productCode").length}`);
        window.history.replaceState({}, "", url);
        window.location.reload();
      }
    });
  };
};

export const updatePriceReferenceForCart = (
  priceReference: string,
  packEntryIds: number[],
  handleOpenPriceRefSnackBar: (count: number) => void,
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const lineItemsToUpdate: LineItemBuilder[] = [];
    const modifiablePackEntries: {
      productCode: string;
      packEntryId: number;
      packCode?: string;
      results?: CartPnaPair;
    }[] = [];

    dispatch({ type: BLOCK_SCREEN, payload: "spinner.updatePriceReferenceForCart" });

    await Promise.all(
      packEntryIds.map(async (packEntryId) => {
        const packageEntry = state.cart.packageEntriesById[packEntryId];
        const productCode = packageEntry.productCode;

        // Skip if sales are enabled for this package entry
        if (packageEntry.saleCampaignEnabled) {
          return;
        }
        const productLineItems = selectLineItemsForProduct(state, { productCode });
        const packCode = quotePackCodeFromEntries(productLineItems);

        // A rejected load keeps `undefined` results — the descriptor falls back to the cache read.
        const results = await loadCartPna({
          productCode,
          priceReference,
          base: state.products.basic[productCode],
          packCode,
        }).catch(() => undefined);
        modifiablePackEntries.push({ productCode, packEntryId, packCode, results });
      }),
    );

    modifiablePackEntries.map(({ productCode, packEntryId, packCode, results }) => {
      const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
      const editedLineItem = lineItems.find((item) => item.get("id") === packEntryId);
      if (editedLineItem) {
        editedLineItem.set("priceReference", priceReference);
        lineItemsToUpdate.push(editedLineItem);

        const productDescriptors = {
          [priceReference]: resolveCartDescriptor(getState(), productCode, priceReference, results, packCode),
        };
        refreshPriceInfoData([editedLineItem], productDescriptors);
      }
    });

    dispatch(sendToRedux(lineItemsToUpdate));
    const updatePriceReferencePromise = dispatch(sendToHybris(lineItemsToUpdate));
    updatePriceReferencePromise.then(() => {
      dispatch({ type: UNBLOCK_SCREEN });
      handleOpenPriceRefSnackBar(uniqBy(modifiablePackEntries, "productCode").length);
    });
  };
};

export const updatePriceReference = (priceReference: string, packageEntryId: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.cartPage.requestInFlight) {
      return;
    }

    const packageEntry = state.cart.packageEntriesById[packageEntryId];
    const productCode = packageEntry.productCode;
    const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
    const packCode = quotePackCodeFromBuilders(lineItems);
    const editedLineItem = lineItems.find((item) => item.get("id") === packageEntryId);

    if (!editedLineItem) {
      return;
    }

    dispatch({ type: BLOCK_SCREEN, payload: "spinner.updatePriceReferenceForCart" });
    try {
      // Reflect the controlled dropdown selection immediately; pricing is filled in below once
      // PnA returns. The pending descriptor retains its base product in useCartPnaQueries.
      editedLineItem.set("priceReference", priceReference);
      dispatch(sendToRedux([editedLineItem]));

      const results = await loadCartPna({
        productCode,
        priceReference,
        base: state.products.basic[productCode],
        packCode,
      }).catch(() => undefined);

      const productDescriptors = getDesciptorsForLineItems(
        getState(),
        productCode,
        lineItems,
        results ? { [priceReference]: results } : undefined,
      );
      refreshPriceInfoData(lineItems, productDescriptors);

      // Persist the selection before reading the server's recalculated entry values.
      const updated = await dispatch(sendToHybris(lineItems));
      if (updated) {
        if (userIsSalesman()) {
          dispatch(sendToRedux(lineItems));
          return;
        }

        const markPricesUnconfirmed = () => {
          const uncertainEntries = selectLineItemsForProduct(getState(), { productCode }).map(newChangeObject);
          uncertainEntries.forEach((entry) => {
            entry.set("netPrice", undefined);
            entry.set("actualNetPrice", undefined);
            entry.set("actualSalePrice", undefined);
            entry.set("msp", undefined);
            entry.set("rsp", undefined);
          });
          dispatch(sendToRedux(uncertainEntries));
          dispatch(addMessage("error", "generic.error.ajax", "translation"));
        };

        const readAndReconcileProduct = async (): Promise<boolean> => {
          let cartData: Awaited<ReturnType<typeof getActiveCartData>>;
          try {
            cartData = await getActiveCartData();
          } catch (error) {
            console.error(error);
            markPricesUnconfirmed();
            return false;
          }
          const serverEntries = Object.values(cartData?.packageEntriesById ?? {}).filter(
            (entry) => entry.productCode === productCode,
          );
          if (!serverEntries.some((entry) => entry.pk === packageEntryId)) {
            console.error(`Updated cart entry ${packageEntryId} is missing from the cart response`);
            markPricesUnconfirmed();
            return false;
          }

          dispatch(reconcileCartProductEntries(serverEntries));
          return true;
        };

        if (!(await readAndReconcileProduct())) {
          return;
        }
        const activeCartId = await resolveActiveCartResourceId();
        const reconciledEntries = selectLineItemsForProduct(getState(), { productCode });
        await dispatch(
          orchestratePnARefresh(reconciledEntries, undefined, activeCartId, {
            preserveServerPrices: true,
            onPersistenceSettled: async () => {
              await readAndReconcileProduct();
            },
          }),
        );
      } else {
        const currentLineItem = getState().cart.packageEntriesById[packageEntryId];
        if (currentLineItem) {
          const rollback = newChangeObject(currentLineItem);
          rollback.set("priceReference", packageEntry.priceReference);
          dispatch(sendToRedux([rollback]));
        }
      }
    } finally {
      dispatch({ type: UNBLOCK_SCREEN });
    }
  };
};
export const updateSalesEnabled = (salesEnabled: boolean, packageEntryId: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const packageEntry = state.cart.packageEntriesById[packageEntryId];
    const productCode = packageEntry.productCode;
    const packCode = quotePackCodeFromEntries(selectLineItemsForProduct(state, { productCode }));

    const productDescriptor = resolveCartDescriptor(
      state,
      productCode,
      packageEntry.priceReference,
      undefined,
      packCode,
    );
    const salesData = calculateSalesCampaign4PDP({ productDescriptor });

    const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
    const editedLineItem = lineItems.filter((item) => item.get("id") === packageEntryId)[0];

    editedLineItem.set("saleCampaignEnabled", salesEnabled);
    if (salesEnabled) {
      const saleForPackage = salesData?.data?.salePerPack[packageEntry.packCode];
      const salesCampaignCode = saleForPackage?.campaignCode;
      editedLineItem.set("saleCampaignCode", salesCampaignCode);
    } else {
      editedLineItem.set("saleCampaignCode", undefined);
    }

    const productDescriptors = getDesciptorsForLineItems(getState(), productCode, lineItems);
    refreshPriceInfoData(lineItems, productDescriptors);
    lineItems.forEach(refreshDeliveryDate);

    // TODO - this is a wrong order, normally we refresh edd before delivery date.
    // Need to take a step back, decide a global order of things and do one flow instead of different flows depending on the parameter that's changing
    const lineItemsByType = groupByFn(
      lineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    if (productDescriptor.availability) {
      refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, productDescriptor.availability!);
    }
    validateEntireProductQty(lineItems, productDescriptor, buildCartMaterialNos(getState()));

    dispatch(sendToRedux(lineItems));
    dispatch(sendToHybris(lineItems));
  };
};

export const updatePoNumber = (customerOrderNumber: string, packageEntryId: number): ThunkResult<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packageEntryId)(state);
    const lineItemUpdate = newUpdatesObject(packageEntry, { customerOrderNumber } as Partial<LineItem>);

    const list = [lineItemUpdate];

    dispatch(sendToRedux(list));
    debounceCall(() => dispatch(sendToHybris(list)));
  };
};

const debounceCall = debounce((fn: () => void) => fn(), 300);

export function navigateToProductContractAction(packEntryId: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const packEntry = state.cart.packageEntriesById[packEntryId];
    const productCode = packEntry.productCode;
    const designation = state.products.basic[productCode].designation;
    fetchContractsOverview().then((data) => navigateToProductContract(data, designation));
  };
}

export function loadPnACartEntry(productCode: string): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const lineItems = selectLineItemsByProduct(state)[productCode];
    const packCode = quotePackCodeFromEntries(lineItems);

    const priceReferences: Dict<boolean> = {};
    lineItems.forEach((entry) => (priceReferences[entry.priceReference] = true));

    const allLoadedOrLoading = Object.keys(priceReferences).every(
      (priceReference) => resolveCartDescriptor(state, productCode, priceReference, undefined, packCode).isLoading,
    );

    if (allLoadedOrLoading) {
      return Promise.resolve();
    }

    const activeCartId = await resolveActiveCartResourceId();
    return dispatch(orchestratePnARefresh(lineItems, undefined, activeCartId));
  };
}
export function loadACartEntry(productCode: string): ThunkResult<Promise<void>> {
  return (dispatch, getState) => {
    const state = getState();
    const lineItems = selectLineItemsByProduct(state)[productCode];
    const packCode = quotePackCodeFromEntries(lineItems);

    const priceReferences: Dict<boolean> = {};
    lineItems.forEach((entry) => (priceReferences[entry.priceReference] = true));

    const allLoadedOrLoading = Object.keys(priceReferences).every(
      (priceReference) =>
        resolveCartDescriptor(state, productCode, priceReference, undefined, packCode).isAvailabilityLoading,
    );

    if (allLoadedOrLoading) {
      return Promise.resolve();
    }

    return dispatch(orchestrateARefreshOnCheckout(lineItems));
  };
}

export function toggleShowPackaging(packEntryId: number, newValue?: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packEntryId)(state);
    // Ensure the product data is available
    const productCode = packageEntry.productCode;

    const lineItemUpdate = newUpdatesObject(packageEntry, {
      isPackagingSelectionOpen: newValue ?? !packageEntry.isPackagingSelectionOpen,
    });

    const list = [lineItemUpdate];
    dispatch(sendToRedux(list));

    const packCode = quotePackCodeFromEntries(selectLineItemsForProduct(state, { productCode }));
    const productDescriptor = resolveCartDescriptor(
      state,
      productCode,
      packageEntry.priceReference,
      undefined,
      packCode,
    );
    if (!productDescriptor.isLoaded && !productDescriptor.isLoading) {
      // Load the entire Cart Entry - rarely users have multiple price references in the same product anyway
      dispatch(loadPnACartEntry(packageEntry.productCode));
    }
  };
}
export function selectPackaging(packEntryId: number, packCode: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const getPackage = (packEntryId: number) => selectPackageEntry(packEntryId)(state);
    const packageEntry = getPackage(packEntryId);

    const productCode = selectProductCodeFromPackage(packEntryId)(state);
    const productDescriptor = resolveCartDescriptor(
      state,
      productCode,
      packageEntry.priceReference,
      undefined,
      quotePackCodeFromEntries(selectLineItemsForProduct(state, { productCode })),
    );

    const allLineItems = selectLineItemsByProduct(state)[productCode].map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );

    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    const editedLineItem = allLineItems.find((item) => item.get("id") === packEntryId)!;
    const previousPackCode = editedLineItem.get("packCode");

    const selectedPackage = productDescriptor.availability!.packagingInformation.find(
      (pack) => pack.packCode === packCode,
    )!;
    if (packCode !== previousPackCode) {
      refreshPackage(editedLineItem, selectedPackage);
    }
    editedLineItem.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
    editedLineItem.removeMessage(MSG_PACKAGE_CHANGED.key);
    editedLineItem.removeMessage(MSG_MISSING_PACKAGE_REGULAR.key);

    const salesData = calculateSalesCampaign4PDP({ productDescriptor });
    const salesForPack = salesData?.data?.salePerPack[packCode];
    if (salesForPack) {
      editedLineItem.set("saleCampaignCode", salesForPack.campaignCode);
      editedLineItem.set("saleCampaignEnabled", true);
    } else {
      editedLineItem.set("saleCampaignCode", "");
      editedLineItem.set("saleCampaignEnabled", false);
    }

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    refreshPriceInfoData(allLineItems, productDescriptors);

    // 3. Refresh the Estimated Delivery Date.
    refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, productDescriptor.availability!);

    validateQty(editedLineItem);
    validateEntireProductQty(allLineItems, productDescriptor, buildCartMaterialNos(state));
    validatePackage(editedLineItem);

    dispatch(sendToRedux(allLineItems));

    dispatch(sendToHybris(allLineItems));
  };
}

export const updateCartCommentThunk = (comment: string, previousComment: string): ThunkResult<void> => {
  return async (dispatch) => {
    apiUpdateCartComment(comment);
    trackCommentActions(comment, previousComment, CommentTargets.CART, comment.length);
    dispatch(updateCartCommentAction(comment));
  };
};

export const UPDATE_CART_COMMENT = "UPDATE_CART_COMMENT";

export const updateCartCommentAction = (comment: string) =>
  ({
    type: UPDATE_CART_COMMENT,
    payload: comment,
  }) as const;
